/** Pending (open) firm invitations with Revoke (F-61). Shown to `users.read` holders. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';

import { api, getErrorMessage, type FirmInvitation } from '../../lib/api.ts';
import { formatDate } from '../../lib/format/date.ts';
import { formatRole } from '../../lib/format/role.ts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Notice,
  Table,
  Td,
  Th,
} from '../ui/index.ts';

export const INVITATIONS_KEY = ['users', 'invitations'] as const;

export function PendingInvitations({ canRevoke }: { canRevoke: boolean }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<FirmInvitation[]>({
    queryKey: INVITATIONS_KEY,
    queryFn: () => api.users.invitations(),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.users.revokeInvitation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: INVITATIONS_KEY }),
  });

  return (
    <Card>
      <CardHeader
        title="Pending invitations"
        description="Invitations sent but not yet accepted. Each link expires after 7 days."
      />
      <CardBody flush>
        {revoke.isError ? <Notice tone="danger">{getErrorMessage(revoke.error)}</Notice> : null}
        {q.isPending ? (
          <p className="argus-settings-inset">Loading invitations...</p>
        ) : q.isError ? (
          <div className="argus-settings-inset">
            <ErrorState
              title="We couldn't load invitations"
              message={getErrorMessage(q.error)}
              onRetry={() => void q.refetch()}
              retrying={q.isFetching}
            />
          </div>
        ) : q.data.length === 0 ? (
          <p className="argus-settings-inset">No pending invitations.</p>
        ) : (
          <Table aria-label="Pending invitations" dense>
            <thead>
              <tr>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Expires</Th>
                {canRevoke ? <Th className="argus-settings-row-actions">Actions</Th> : null}
              </tr>
            </thead>
            <tbody>
              {q.data.map((inv) => (
                <tr key={inv.invitation_id}>
                  <Td>{inv.email}</Td>
                  <Td>{formatRole(inv.role)}</Td>
                  <Td>{formatDate(inv.expires_at * 1000)}</Td>
                  {canRevoke ? (
                    <Td className="argus-settings-row-actions">
                      <Button
                        size="sm"
                        variant="danger-outline"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(inv.invitation_id)}
                      >
                        Revoke
                      </Button>
                    </Td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}
